import { type ChangeEvent, type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Box,
  Button,
  Devider,
  FilterContext,
  Flex,
  Text,
  InputSearch,
  UploadMediaContainer,
  ModalContext,
  Checkbox,
  MultiFileUploadContext,
  PageViewToggle,
  PageViewTypeEnum,
  SearchContext,
} from 'dash-ui';
import {
  MediaFileTypesFilterEnum,
  ProgressTrackingEvent,
  ProgressTrackingTypesEnum,
} from 'dash-common';

import { useUploadMediaFile } from 'common/hooks/useUploadMediaFile';
import { BulkActionsContext } from 'common/contexts/bulkActionsContext/bulkActionsContext';
import { useObserveProgressTrackingByTypeSubscription } from 'gql/generated.graphql';
import renderBranch from 'common/helpers/renderBranch';
import { stickyTopBarStyles } from 'containers/properties/helpers/stickyTopBarStyles';
import {
  FlexWraper,
  PaginationPerPageSelectWraper,
  SelectWraper,
} from 'containers/properties/PropertyMedia/components/MediaPageHeader/MediaPageHeader.style';

const SelectAllContainer = styled(Flex)`
  border-radius: ${({ theme }) => theme.space * 2}px;
  border: 1px solid ${({ theme }) => theme.colors.n40};
`;

const PageWrapper = styled(Box)<{ top?: number }>`
  ${stickyTopBarStyles}
`;

const MediaPageHeader: FC<{
  propertyId: string;
  folderId?: string;
  disabled?: boolean;
}> = ({ propertyId, folderId, disabled = false }) => {
  const { t } = useTranslation(['pages', 'common']);
  const { selected, setIsAllSelected } = useContext(BulkActionsContext);
  const { setFilterState } = useContext(FilterContext);
  const { setSearchSlug } = useContext(SearchContext);
  const { toggleModal } = useContext(ModalContext);
  const [isOpenedUploadContainer, setIsOpenedUploadContainer] = useState(false);
  const uploadMediaFile = useUploadMediaFile();
  const { addProgressTrackingEvent } = useContext(MultiFileUploadContext);
  const [inputValue, setInputValue] = useState('');

  useObserveProgressTrackingByTypeSubscription({
    variables: { type: ProgressTrackingTypesEnum.FILE_UPLOAD },
    onData({ data }) {
      const incomingProgressTrackingEvent =
        data?.data?.observeProgressTrackingByType.document;

      if (incomingProgressTrackingEvent) {
        addProgressTrackingEvent(
          incomingProgressTrackingEvent as ProgressTrackingEvent
        );
      }
    },
  });

  return (
    <PageWrapper w="100%" top={folderId ? 202 : 244}>
      <Flex px={6} py={4} alignItems="center" justifyContent="space-between">
        <Text lg>{t('pages:propertyPage.mediaFiles')}</Text>
        <PageViewToggle
          defaultButton={PageViewTypeEnum.CARDS}
          buttons={[
            { icon: 'apps', viewType: PageViewTypeEnum.CARDS },
            { icon: 'list', viewType: PageViewTypeEnum.LIST },
          ]}
        />
      </Flex>
      <Devider />
      <Flex py={4} px={6} alignItems="center">
        {renderBranch(
          disabled,
          null,
          <SelectAllContainer minWidth={122} mr={3} p={2}>
            <Flex>
              <Checkbox
                mr={2}
                onChange={(checked) => setIsAllSelected(checked)}
              />
            </Flex>
            <Flex>
              <Text>{t('common:labels:selectAll')}</Text>
            </Flex>
          </SelectAllContainer>
        )}
        <FlexWraper>
          <InputSearch
            w="100%"
            value={inputValue}
            placeholder={t('pages:propertyPage.searchMedia') || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchSlug(e.target.value);
              setInputValue(e.target.value);
            }}
            onClear={() => {
              setSearchSlug('');
              setInputValue('');
            }}
          />
          <Flex alignItems="center">
            <FlexWraper>
              <PaginationPerPageSelectWraper
                label={t('common:labels.show') || ''}
                options={[20, 40, 60, 100]}
                defaultValue={40}
              />
              <SelectWraper
                isMulti
                onChanged={(_: string, values?: string[]) =>
                  setFilterState(
                    'fileTypes',
                    values as MediaFileTypesFilterEnum[]
                  )
                }
                placeholder={t('pages:propertyPage.allFileTypes')}
                options={Object.values(MediaFileTypesFilterEnum).map(
                  (item) => ({
                    label: item,
                    value: item,
                  })
                )}
              />
            </FlexWraper>
            {renderBranch(
              disabled,
              null,
              <Button
                onClick={() => toggleModal(true, 'SetFolderNameModal')}
                variation="primary"
                icon="plus"
                mr={4}
                w={119}
              >
                {t('common:labels.folder')}
              </Button>
            )}
            {renderBranch(
              disabled,
              null,
              <Button
                w={119}
                variation="primary"
                icon="plus"
                onClick={() => setIsOpenedUploadContainer(true)}
              >
                {t('common:labels.file')}
              </Button>
            )}
          </Flex>
        </FlexWraper>
      </Flex>
      <Devider />

      <UploadMediaContainer
        mb={selected.length > 0 ? 20 : 0}
        visible={isOpenedUploadContainer}
        onClose={() => setIsOpenedUploadContainer(false)}
        onUpload={(filesToUpload) => {
          filesToUpload.forEach((fileUpload) => {
            uploadMediaFile({
              fileUpload,
              propertyId,
              mediaFolderId: folderId,
            });
          });
        }}
      />
    </PageWrapper>
  );
};

export default MediaPageHeader;
